<script setup>
import { useAppStore } from '@/stores/App'

// Vars
const app = useAppStore()

// SEO
useSeoMeta({
    title: 'Privacy Policy',
    ogTitle: 'Privacy Policy',
    description: 'satYatunes Privacy Policy',
    ogDescription: 'satYatunes Privacy Policy',
})
</script>

<template>
    <v-row id="privacy" justify="center" align="start" class="mb-16 slide-from-top">
        <v-col cols="12" sm="10" class="mt-4 text-center">
            <!-- <div class="text-h4 font-weight-medium">Privacy Policy</div> -->
            <app-section-header :id="`prv`" :title="`Privacy Policy`" :subtitle="``" />
            <div>Last updated: {{ app.lastUpdatedDates.privacyPolicy }}</div>
            <br />
            <div class="text-left">
                <p>
                    This Privacy Policy for satYatunes (<span class="font-weight-medium"
                        >"Company", "we", "us" or "our" </span
                    >), describes how and why we might collect, store, use, and/or share (<span
                        class="font-weight-medium"
                        >"process"</span
                    >) your information when you use our services (<span class="font-weight-medium">"Services"</span>),
                    such as when you:
                </p>
                <ul class="ml-8 mt-4">
                    <li>Visit our website at satyatunes.com</li>
                    <li>Engage with us in other related ways, including any sales, support, marketing or events</li>
                </ul>
                <br />
                <p>
                    <b>Questions or concerns?</b> Reading this privacy policy will help you understand your privacy
                    rights and choices. If you do not agree with our policies and practices, please do not use our
                    Services.
                </p>
                <p class="mt-4">
                    <b>CONSENT: </b>We collect and use personal data only to the extent necessary to provide and improve
                    our content and services. By using our Services, you hereby consent to our Privacy Policy and agree
                    to its terms.
                </p>
            </div>
        </v-col>
        <v-col cols="12" sm="10">
            <v-expansion-panels class="mb-10 elevation-0">
                <v-expansion-panel class="elevation-0">
                    <v-expansion-panel-title class="font-weight-bold">
                        WHAT INFORMATION DO WE COLLECT?
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <p class="font-weight-bold">Personal information you disclose to us</p>
                        We collect personal information that you voluntarily provide to us when you express an interest
                        in obtaining information about us or our products and Services, when you participate in
                        activities on the Services, or otherwise when you contact us.

                        <p class="mt-4 font-weight-bold">
                            Sensitive Information - We do not process sensitive information.
                        </p>
                        <p>
                            All personal information that you provide to us must be true, complete, and accurate, and
                            you must notify us of any changes to such personal information.
                        </p>
                        <p class="mt-4 pb-0 font-weight-bold">Information automatically collected</p>
                        <p class="pt-0">
                            We automatically collect certain information when you visit, use, or navigate the Services.
                            The information does not reveal your specific identity (like your name or contact
                            information) but may include device and usage information, such as your IP address, browser
                            and device charteristics, operating system, language preferences, referring URLs, device
                            name, country, location, information about how and when you use our Services, and other
                            technical information. This information is primarily needed to maintain the security and
                            operation of our Services, and for out internal analytics and reporting purposes.
                        </p>
                        <p class="mt-2">
                            Like many businesses, we also collect information through cookies and simiar technologies.
                        </p>
                    </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel class="elevation-0">
                    <v-expansion-panel-title class="font-weight-bold">
                        HOW DO WE PROCESS YOUR INFORMATION?
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <p>
                            We process your information to provide, improve, and administer our Services, communicate
                            with you, for security and fraud prevention, and to comply with law. We may also process
                            your information for other purposes with your consent.
                        </p>
                        <p>
                            We process your personal information for a variety of reasons, depending on how you interact
                            with our Services, including:
                        </p>
                        <ul class="ml-8 pt-4">
                            <li>
                                To deliver and facilitate delivery of services to the user. We may process your
                                information to provide you with the requested service
                            </li>
                            <li>
                                To respond to user inquiries/offer support to users. We may process your information to
                                respond to your inquiries and solve any potential issues you might have with the
                                requested service
                            </li>
                            <li>
                                To send administrative information to you. We may process your information to send you
                                details about our products and services, changes to our terms and policies, and other
                                similar information
                            </li>
                            <li>
                                To fulfil and manage your orders. We may process your information to fulfil and manage
                                your orders, payments, returns, and exchanges made through the Services
                            </li>
                            <li>
                                To request feedback. We may process your information when necessary to request feedback
                                and to contact you about your use of our Services
                            </li>
                            <li>
                                To send you marketing and promotional communications. We may process the personal
                                information you send to us for our marketing purposes, if this is in accordance with
                                your marketing preferences. You can opt out of our marketing emails at any time. For
                                more information, see "WHAT ARE YOUR PRIVACY RIGHTS?" below.
                            </li>
                            <li>
                                To deliver targeted advertising to you. We may process your information to develop and
                                display personalized content and advertising tailored to your interests, location, and
                                more
                            </li>
                            <li>
                                To protect our Services. We may process your information as part of our efforts to keep
                                our Services safe and secure, including fraud monitoring and prevention
                            </li>
                            <li>
                                To identify usage trends. We may process information about how you use our Services to
                                better understand how they are being used so we can improve them
                            </li>
                            <li>
                                To determine the effectiveness of our marketing and promotional campaigns. We may
                                process your information to better understand how to provide marketing and promotional
                                campaigns that are most relevant to you
                            </li>
                            <li>
                                To save or protect an individual's vital interest. We may process your information when
                                necessary to save or protect an individual's vital interest, such as to prevent harm
                            </li>
                        </ul>
                    </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel>
                    <v-expansion-panel-title class="font-weight-bold">
                        WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <p>
                            We may need to share your personal information in the following situations and/or with the
                            following third parties:
                        </p>
                        <ul class="ml-8 mt-4">
                            <li>
                                <span class="pb-0 font-weight-bold">Business Transfers.</span> We may share or transfer
                                your information in connection with, or during negotiations of, any merger, sale of
                                company assets, financing, or acquisition of all or a portion of our business to another
                                company.
                            </li>
                            <li>
                                <span class="pb-0 font-weight-bold">Affiliates.</span> We may share your information
                                with our affiliates, in which case we will require those affiliates to honor this
                                privacy notice. Affiliates include our parent company and any subsidiaries, joint
                                venture partners, or other companies that we control or that are under common control
                                with us.
                            </li>
                            <li>
                                <span class="pb-0 font-weight-bold">Business Partners.</span> We may share your
                                information with our business partners to offer you certain products, services, or
                                promotions.
                            </li>
                        </ul>
                        <p class="mt-4">
                            We use following third party services/products to provide some of the Services. For more
                            information please refer to their respective privacy policies.
                        </p>
                        <ul class="ml-8 mt-4">
                            <li>PayPal</li>
                            <li>Google Analytics</li>
                            <li>Mailchimp</li>
                            <li>Brevo</li>
                            <li>Mailcheck</li>
                            <li>Kickbox</li>
                            <li>RapidAPI</li>
                            <li>EVA</li>
                        </ul>
                    </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel>
                    <v-expansion-panel-title class="font-weight-bold">
                        DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <p>
                            We may use cookies and similar tracking technologies (like web beacons and pixels) to access
                            or store information. Specific information about how we use such technologies and how you
                            can refuse certain cookies is set out in our
                            <nuxt-link :to="`/cookie`" aria-label="satYatunes cookie policy">Cookie Policy</nuxt-link>.
                        </p>
                    </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel>
                    <v-expansion-panel-title class="font-weight-bold">
                        IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <p>We may transfer, store, and process your information in countries other than your own.</p>
                        <p>
                            Our servers are located in the United States. If you are accessing our Services from outside
                            the United States, please be aware that your information may be transferred to, stored, and
                            processed by us in our facilities and by those third parties with whom we may share your
                            personal information (see "WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?"
                            above), in the United States, and other countries.
                        </p>
                        <p>
                            If you are a resident in the European Economic Area (EEA) or United Kingdom (UK), or
                            Switzerland, then these countries may not necessarily have data protection laws or other
                            similar laws as comprehensive as those in your country. However, we will take all necessary
                            measures to protect your personal information in accordance with this privacy notice and
                            applicable law.
                        </p>
                    </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel>
                    <v-expansion-panel-title class="font-weight-bold">
                        HOW LONG DO WE KEEP YOUR INFORMATION?
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <p>
                            We keep your information for as long as necessary to fulfill the purposes outlined in this
                            privacy notice unless otherwise required by law.
                        </p>
                        <p class="mt-4">
                            We will only keep your personal information for as long as it is necessary for the purposes
                            set out in this privacy notice, unless a longer retention period is required or permitted by
                            law (such as tax, accounting, or other legal requirements). No purpose in this notice will
                            require us keeping your personal information for longer than 2 years.
                        </p>
                        <p class="mt-4">
                            When we have no ongoing legitimate business need to process your personal information, we
                            will either delete or anonymize such information, or, if this is not possible (for example,
                            because your personal information has been stored in backup archives), then we will securely
                            store your personal information and isolate it from any further processing until deletion is
                            possible.
                        </p>
                    </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel>
                    <v-expansion-panel-title class="font-weight-bold">
                        WHAT ARE YOUR PRIVACY RIGHTS?
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <p>
                            <span class="pb-0 font-weight-bold">Withdrawing your consent:</span> If we are relying on
                            your consent to process your personal information, which may be express and/or implied
                            consent depending on the applicable law, you have the right to withdraw your consent at any
                            time. You can withdraw your consent at any time by contacting us by using the contact
                            details provided in the section "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?" below.
                        </p>
                        <p class="mt-4">
                            However, please note that this will not affect the lawfulness of the processing before its
                            withdrawal nor, when applicable law allows, will it affect the processing of your personal
                            information conducted in reliance on lawful processing grounds other than consent.
                        </p>
                        <p class="mt-4">
                            <span class="pb-0 font-weight-bold">
                                Opting out of marketing and promotional communications:
                            </span>
                            You can unsubscribe from our marketing and promotional communications at any time by
                            clicking on the unsubscribe link in the emails that we send, or by contacting us using the
                            details provided in the section "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?" below. You will
                            then be removed from the marketing lists. However, we may still communicate with you — for
                            example, to send you service-related messages that are necessary for the administration and
                            use of your account, to respond to service requests, or for other non-marketing purposes.
                        </p>
                        <p class="mt-4">
                            <span class="pb-0 font-weight-bold"> Cookies and similar technologies: </span>
                            Most Web browsers are set to accept cookies by default. If you prefer, you can usually
                            choose to set your browser to remove cookies and to reject cookies. If you choose to remove
                            cookies or reject cookies, this could affect certain features or services of our Services.
                        </p>
                    </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel>
                    <v-expansion-panel-title class="font-weight-bold">
                        CONTROLS FOR DO-NOT-TRACK FEATURES
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <p>
                            Most web browsers and some mobile operating systems and mobile applications include a
                            Do-Not-Track ("DNT") feature or setting you can activate to signal your privacy preference
                            not to have data about your online browsing activities monitored and collected. At this
                            stage no uniform technology standard for recognizing and implementing DNT signals has been
                            finalized. As such, we do not currently respond to DNT browser signals or any other
                            mechanism that automatically communicates your choice not to be tracked online. If a
                            standard for online tracking is adopted that we must follow in the future, we will inform
                            you about that practice in a revised version of this privacy policy.
                        </p>
                    </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel>
                    <v-expansion-panel-title class="font-weight-bold">
                        DO WE MAKE UPDATES TO THIS NOTICE?
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <p>Yes, we will update this notice as necessary to stay compliant with relevant laws.</p>
                        <p class="mt-4">
                            We may update this privacy notice from time to time. The updated version will be indicated
                            by an updated "Revised" date and the updated version will be effective as soon as it is
                            accessible. If we make material changes to this privacy notice, we may notify you either by
                            prominently posting a notice of such changes or by directly sending you a notification. We
                            encourage you to review this privacy notice frequently to be informed of how we are
                            protecting your information.
                        </p>
                    </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel>
                    <v-expansion-panel-title class="font-weight-bold">
                        HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <p>
                            If you have questions or comments about this notice, you may email us at
                            satyatunes(at)yahoo(dot)com or by post to:
                        </p>
                        <company-address-india />
                    </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel>
                    <v-expansion-panel-title class="font-weight-bold">
                        HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <p>
                            Based on the applicable laws of your country, you may have the right to request access to
                            the personal information we collect from you, change that information, or delete it. To
                            request to review, update, or delete your personal information, please contact us via our
                            <nuxt-link :to="`/support`" aria-label="satYatunes support center">
                                support center </nuxt-link
                            >.
                        </p>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
        </v-col>
    </v-row>
</template>

<style scoped></style>
